import { Trans } from "@lingui/react";
import { WIcon } from "@mobility-item-page/client";
import { Clickable } from "@warp-ds/react";
import { useEffect, useState } from "react";
import { useCannedMessages } from "../utils/cannedMessages.ts";
import { useTracking } from "../utils/pulseHelper.ts";
import { ScreenReaderSeparator } from "./ScreenReaderSeparator.tsx";

interface CannedMessageProps {
  message: string;
  onClick?: () => void;
  active?: boolean;
}

const CannedMessage = ({ message, onClick, active = false }: CannedMessageProps) => {
  return (
    <div
      className={`relative py-4 px-8 border-0 rounded-4 text-xs inline-flex s-text gap-4 items-center ${active ? "s-bg-primary-subtle" : "s-bg-subtle"}`}
    >
      {active ? <WIcon name="Check" /> : <WIcon name="Plus" />}
      <ScreenReaderSeparator />
      <Clickable checkbox onClick={onClick}>
        {message}
      </Clickable>
    </div>
  );
};

interface CannedMessageGroupProps {
  message: string;
  setMessage: (newMessage: string) => void;
}

export const CannedMessageGroup = ({ message, setMessage }: CannedMessageGroupProps) => {
  const { cannedMessages } = useCannedMessages();
  type CannedMessageId = (typeof cannedMessages)[number]["id"];
  type CannedMessageFlags = Record<CannedMessageId, boolean>;
  const [activeCannedMessage, setActiveCannedMessage] = useState<CannedMessageFlags>(
    Object.fromEntries(Object.keys(cannedMessages).map((key) => [key, false])) as CannedMessageFlags,
  );
  const { trackCannedMessageClick } = useTracking();

  useEffect(() => {
    setActiveCannedMessage(
      Object.fromEntries(cannedMessages.map(({ id, message: m }) => [id, message.includes(m)])) as CannedMessageFlags,
    );
  }, [message, cannedMessages.map]);

  const handleCannedMessageClick = (id: CannedMessageId, cannedMessage: string) => {
    const isActive = activeCannedMessage[id];
    const newActiveState = !isActive;

    if (newActiveState) {
      const newMessage = message ? `${message} ${cannedMessage}` : cannedMessage;
      setMessage(newMessage);
      trackCannedMessageClick(cannedMessage);
    } else {
      const newMessage = message.replace(cannedMessage, "").trim();
      setMessage(newMessage);
    }

    setActiveCannedMessage((prevState) => ({
      ...prevState,
      [id]: newActiveState,
    }));
  };

  if (cannedMessages.length === 0) {
    return null;
  }

  return (
    <fieldset>
      <legend className="s-text-subtle text-xs mb-8">
        <Trans
          id="dealer.contactForm.cannedMessage.insert.info"
          message="Click on a question to insert it into the message field."
        />
      </legend>
      <div className="flex items-center flex-wrap gap-8">
        {cannedMessages.map(({ id, message: cannedMessage }) => {
          return (
            <CannedMessage
              key={id}
              message={cannedMessage}
              onClick={() => handleCannedMessageClick(id, cannedMessage)}
              active={activeCannedMessage[id]}
            />
          );
        })}
      </div>
    </fieldset>
  );
};
